import { useState, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { Input } from '@antimatterhq/ui/components/input';
import { Icon } from '@antimatterhq/ui/components/icon';
import { ScrollArea } from '@antimatterhq/ui/components/scrollArea';
import { cn } from '@/lib/utils';
import {
  recentQueriesAtom,
  openQueryTabAtom,
  queryTabIdsAtom,
} from '@/atoms/query/queryAtoms';

interface OpenExistingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QueryOpenExistingDialog({
  open,
  onOpenChange,
}: OpenExistingDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const recentQueries = useAtomValue(recentQueriesAtom);
  const openTab = useSetAtom(openQueryTabAtom);
  const tabIds = useAtomValue(queryTabIdsAtom);

  const filteredQueries = useMemo(() => {
    if (!searchQuery.trim()) return recentQueries;

    const query = searchQuery.toLowerCase();
    return recentQueries.filter((q) => q.name.toLowerCase().includes(query));
  }, [recentQueries, searchQuery]);

  const handleSelectQuery = (queryId: string) => {
    openTab(queryId);
    onOpenChange(false);
    setSearchQuery('');
  };

  const isTabOpen = (queryId: string) => tabIds.includes(queryId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Open existing</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search input */}
          <div className="relative">
            <Icon
              icon="lucide:search"
              className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
            />
            <Input
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>

          {/* Tabs for filtering (could be extended in future) */}
          <div className="flex items-center gap-4 border-b">
            <button className="border-primary border-b-2 pb-2 text-sm font-medium">
              For you
            </button>
            <button className="text-muted-foreground hover:text-foreground pb-2 text-sm">
              All
            </button>
          </div>

          {/* Query list */}
          <ScrollArea className="h-[400px]">
            <div className="space-y-4">
              {/* Recent queries section */}
              {filteredQueries.length > 0 && (
                <div>
                  <h3 className="text-muted-foreground mb-2 text-sm font-medium">
                    Recents ({filteredQueries.length})
                  </h3>
                  <div className="space-y-1">
                    {filteredQueries.map((query) => (
                      <button
                        key={query.id}
                        onClick={() => handleSelectQuery(query.id)}
                        className={cn(
                          'hover:bg-muted flex w-full items-center gap-3 rounded-md p-3 text-left transition-colors',
                          isTabOpen(query.id) && 'bg-muted',
                        )}
                      >
                        <Icon
                          icon="lucide:folder-open"
                          className="text-muted-foreground h-4 w-4 flex-shrink-0"
                        />
                        <div className="flex-1 overflow-hidden">
                          <div className="truncate text-sm font-medium">
                            {query.name}
                          </div>
                          <div className="text-muted-foreground truncate text-xs">
                            Last opened:{' '}
                            {new Date(query.lastOpened).toLocaleString()}
                          </div>
                        </div>
                        {isTabOpen(query.id) && (
                          <span className="text-muted-foreground text-xs">
                            Open
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No results message */}
              {filteredQueries.length === 0 && (
                <div className="text-muted-foreground py-8 text-center text-sm">
                  {searchQuery
                    ? 'No queries found matching your search'
                    : 'No recent queries'}
                </div>
              )}

              {/* Favorites section placeholder */}
              <div className="border-t pt-4">
                <h3 className="text-muted-foreground mb-2 text-sm font-medium">
                  Favorites (0)
                </h3>
                <p className="text-muted-foreground text-sm">
                  No favorite assets
                </p>
              </div>
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
